import { useEffect, useState } from 'react';
import {
  SlotsAndUnavailability,
  SlotsAndUnavailabilityWithRefetch,
  TimeSlot,
} from '../../components/TimeScheduleInput/types';
import { trackEvent } from '../../utils/eventTracking';
import { getBookingSlots, getPracticeSettings } from '../../api/api';
import { BookingSlotsResponseList } from '../../api/types';
import { BOOKING_HORIZON_DAYS } from '../../constants';
import { getSnowplowEvent } from './getSnowplowEvent';
import { PracticeSettingsProps } from './types';

function isFirstSlot(i: number) {
  return i === 0;
}

function addAvailableSlot(
  timeSlots: TimeSlot[],
  slotTime: number,
  professionId: string
) {
  timeSlots.push({
    date: new Date(slotTime),
    disabled: false,
    profession: professionId,
  });
}

function addUnavailableSlot(
  timeSlots: TimeSlot[],
  slotTime: number,
  professionId: string
) {
  timeSlots.push({
    date: new Date(slotTime),
    disabled: true,
    profession: professionId,
  });
}

function isCurrentSlotNotContinuousWithExistingSlots(
  s: Record<'startTimeInclusive' | 'endTimeExclusive', number>,
  slots: [Record<'startTimeInclusive' | 'endTimeExclusive', number>],
  i: number
) {
  return (
    s.startTimeInclusive !== slots[i - 1].endTimeExclusive &&
    new Date(s.startTimeInclusive).getDate() ===
      new Date(slots[i - 1].endTimeExclusive).getDate()
  );
}
export function transformTimeSlotsList(slots: BookingSlotsResponseList) {
  const timeSlots = [] as TimeSlot[];
  const slotsInterval: number =
    slots[0] && slots[0].endTimeExclusive - slots[0].startTimeInclusive;

  slots.forEach((s, i) => {
    if (isFirstSlot(i)) {
      addAvailableSlot(timeSlots, s.startTimeInclusive, s.professionId);
    } else {
      if (isCurrentSlotNotContinuousWithExistingSlots(s, slots, i)) {
        // Fill the gap with unavailable slots
        let counter = 0;
        while (
          s.startTimeInclusive >
          slots[i - 1].endTimeExclusive + slotsInterval * counter
        ) {
          addUnavailableSlot(
            timeSlots,
            slots[i - 1].endTimeExclusive + slotsInterval * counter,
            s.professionId
          );
          counter++;
        }
      }
      addAvailableSlot(timeSlots, s.startTimeInclusive, s.professionId);
    }
  });
  return timeSlots;
}

async function fetchSlotsAndDayMessages() {
  const now = Date.now();
  const nextWeek = new Date().setDate(
    new Date().getDate() + BOOKING_HORIZON_DAYS - 1
  );
  const response = await getBookingSlots(now, nextWeek);
  const availableSlots = transformTimeSlotsList(response.slots);
  const unavailabilityMessages = response.dayMessages;
  return { availableSlots, unavailabilityMessages };
}

export const useAppointmentSlots = () => {
  const [nurseAppointmentSlots, setNurseAppointmentSlots] =
    useState<SlotsAndUnavailability>();
  const [doctorAppointmentSlots, setDoctorAppointmentSlots] =
    useState<SlotsAndUnavailability>();

  const fetchSlots = async () => {
    fetchSlotsAndDayMessages().then((res) => {
      const nurseSlotsAndDayMessages: SlotsAndUnavailability = {
        availableSlots: res.availableSlots.filter(
          (slot) => slot.profession === 'nurse'
        ),
        unavailabilityMessages: res.unavailabilityMessages.map((dayMsg) => ({
          day: new Date(Date.parse(dayMsg.day)),
          message: dayMsg.message,
        })),
      };
      const doctorSlotsAndDayMessages: SlotsAndUnavailability = {
        availableSlots: res.availableSlots.filter(
          (slot) => slot.profession === 'doctor'
        ),
        unavailabilityMessages: res.unavailabilityMessages.map((dayMsg) => ({
          day: new Date(Date.parse(dayMsg.day)),
          message: dayMsg.message,
        })),
      };

      setNurseAppointmentSlots(nurseSlotsAndDayMessages);
      setDoctorAppointmentSlots(doctorSlotsAndDayMessages);

      trackEvent(getSnowplowEvent(nurseSlotsAndDayMessages));
      trackEvent(getSnowplowEvent(doctorSlotsAndDayMessages));
    });
  };

  useEffect(() => {
    fetchSlots();
  }, []);
  return {
    nurseSlots: nurseAppointmentSlots,
    doctorSlots: doctorAppointmentSlots,
    refetch: fetchSlots,
  } as const;
};

async function fetchPracticeSettings() {
  return await getPracticeSettings();
}

export const usePracticeSettings = (authenticated: boolean | undefined) => {
  const [practiceSettings, setPracticeSettings] =
    useState<PracticeSettingsProps>({
      isAppointmentListEnabled: false,
      partnership: 'UNKNOWN',
      isNurseBookingEnabled: false,
    });

  useEffect(() => {
    fetchPracticeSettings().then((res) => {
      setPracticeSettings({
        isAppointmentListEnabled: res.appointmentListEnabled,
        partnership: res.partnership,
        isNurseBookingEnabled: res.isNurseBookingEnabled,
      });
    });
  }, [authenticated]);
  return practiceSettings;
};
